<script setup lang="ts">
import { ref, watch, nextTick, computed } from 'vue';
import { useCommandBar, type SearchResult } from '../composables/useCommandBar';

const {
  isOpen,
  searchQuery,
  results,
  loading,
  selectedIndex,
  recentSearches,
  recentItems,
  allResults,
  searchMode,
  showShortcutsHelp,
  close,
  search,
  selectResult,
  clearRecentSearches,
  clearRecentItems,
  navigateUp,
  navigateDown,
  selectCurrent,
  formatKeyForDisplay,
} = useCommandBar();

const searchInput = ref<HTMLInputElement | null>(null);

// Focus input when command bar opens
watch(isOpen, async (open) => {
  if (open) {
    await nextTick();
    searchInput.value?.focus();
  }
});

// Trigger search when query changes
watch(searchQuery, (query) => {
  search(query);
});

// Handle clicking on a result
function handleResultClick(result: SearchResult) {
  selectResult(result);
}

// Handle clicking on a recent search
function handleRecentClick(query: string) {
  searchQuery.value = query;
}

// Handle clicking overlay to close
function handleOverlayClick(event: MouseEvent) {
  if ((event.target as HTMLElement).classList.contains('command-bar-overlay')) {
    close();
  }
}

// Handle keyboard navigation in input
function handleInputKeydown(event: KeyboardEvent) {
  switch (event.key) {
    case 'ArrowUp':
      event.preventDefault();
      navigateUp();
      break;
    case 'ArrowDown':
      event.preventDefault();
      navigateDown();
      break;
    case 'Enter':
      event.preventDefault();
      selectCurrent();
      break;
    case 'Escape':
      event.preventDefault();
      close();
      break;
    case 'Tab':
      // Prevent Tab from leaving the command bar - use arrow keys instead
      event.preventDefault();
      if (event.shiftKey) {
        navigateUp();
      } else {
        navigateDown();
      }
      break;
    case 'Backspace':
      // If query is '>' and user presses backspace, switch to search mode
      if (searchQuery.value === '>') {
        event.preventDefault();
        searchQuery.value = '';
      }
      break;
  }
}

// Get category icon
function getCategoryIcon(type: string): string {
  switch (type) {
    case 'action':
      return 'bi-lightning';
    case 'job':
      return 'bi-briefcase';
    case 'catalogue':
      return 'bi-book';
    case 'supplier':
      return 'bi-truck';
    case 'order':
      return 'bi-cart';
    case 'contact':
      return 'bi-person-lines-fill';
    case 'document':
      return 'bi-file-earmark';
    case 'recent':
      return 'bi-clock-history';
    default:
      return 'bi-circle';
  }
}

// Get category label
function getCategoryLabel(type: string): string {
  switch (type) {
    case 'action':
      return 'Commands';
    case 'job':
      return 'Jobs';
    case 'catalogue':
      return 'Catalogue';
    case 'supplier':
      return 'Suppliers';
    case 'order':
      return 'Orders';
    case 'contact':
      return 'Contacts';
    case 'document':
      return 'Documents';
    case 'recent':
      return 'Recently Viewed';
    default:
      return type;
  }
}

// Check if a result is selected
function isSelected(result: SearchResult): boolean {
  const index = allResults.value.indexOf(result);
  return index === selectedIndex.value;
}

// Get the index of a result in the flat array
function getResultIndex(result: SearchResult): number {
  return allResults.value.indexOf(result);
}

// Highlight matching text in a string
function highlightMatch(text: string, query: string): string {
  if (!query || !text) return text;

  // Don't highlight for command mode prefix
  const effectiveQuery = query.startsWith('>') ? query.slice(1).trim() : query;
  if (!effectiveQuery) return text;

  const textLower = text.toLowerCase();
  const queryLower = effectiveQuery.toLowerCase();
  const index = textLower.indexOf(queryLower);

  if (index >= 0) {
    const before = text.substring(0, index);
    const match = text.substring(index, index + effectiveQuery.length);
    const after = text.substring(index + effectiveQuery.length);
    return `${before}<mark>${match}</mark>${after}`;
  }

  return text;
}

// Format shortcut for display
function formatShortcut(shortcut?: string): string {
  if (!shortcut) return '';
  return formatKeyForDisplay(shortcut);
}

// Scroll selected item into view
watch(selectedIndex, async () => {
  await nextTick();
  const selected = document.querySelector('.command-result-item.selected');
  selected?.scrollIntoView({ block: 'nearest' });
});

// Computed property for placeholder text
const placeholderText = computed(() => {
  if (searchMode.value === 'commands') {
    return 'Type a command...';
  }
  return 'Search or type > for commands...';
});

// Check if we should show a category
function shouldShowCategory(items: SearchResult[]): boolean {
  return items.length > 0;
}

// Close shortcuts help
function closeShortcutsHelp() {
  showShortcutsHelp.value = false;
}
</script>

<template>
  <Teleport to="body">
    <!-- Command Bar Modal -->
    <Transition name="fade">
      <div v-if="isOpen" class="command-bar-overlay" @click="handleOverlayClick">
        <div class="command-bar">
          <!-- Search Input -->
          <div class="command-bar-header">
            <i class="bi bi-search search-icon"></i>
            <input
              ref="searchInput"
              v-model="searchQuery"
              type="text"
              class="command-bar-input"
              :placeholder="placeholderText"
              autocomplete="off"
              @keydown="handleInputKeydown"
            />
            <div class="header-badges">
              <span v-if="searchMode === 'commands'" class="mode-badge">
                <i class="bi bi-lightning-fill"></i> Commands
              </span>
              <kbd class="esc-hint">ESC</kbd>
            </div>
          </div>

          <!-- Results -->
          <div class="command-bar-results">
            <!-- Loading spinner -->
            <div v-if="loading" class="command-bar-loading">
              <span class="spinner-border spinner-border-sm text-primary me-2"></span>
              Searching...
            </div>

            <template v-else-if="!searchQuery.trim()">
              <!-- Empty state: show quick actions and recent items -->

              <!-- Quick Actions (always show when empty) -->
              <div v-if="shouldShowCategory(results.actions)" class="command-category">
                <div class="command-category-header">
                  <i :class="getCategoryIcon('action')" class="me-2"></i>
                  {{ getCategoryLabel('action') }}
                  <span class="category-hint">Type &gt; to filter commands</span>
                </div>
                <div class="command-category-items">
                  <button
                    v-for="result in results.actions.slice(0, 6)"
                    :key="result.id"
                    class="command-result-item"
                    :class="{ selected: isSelected(result) }"
                    tabindex="-1"
                    @click="handleResultClick(result)"
                    @mouseenter="selectedIndex = getResultIndex(result)"
                  >
                    <i :class="result.icon" class="result-icon"></i>
                    <span class="result-title">{{ result.title }}</span>
                    <kbd v-if="result.shortcut" class="result-shortcut">{{ formatShortcut(result.shortcut) }}</kbd>
                  </button>
                </div>
              </div>

              <!-- Recent Items -->
              <div v-if="recentItems.length > 0" class="command-category">
                <div class="command-category-header">
                  <i class="bi bi-clock-history me-2"></i>
                  Recently Viewed
                  <button class="btn-clear-recent" tabindex="-1" @click.stop="clearRecentItems">Clear</button>
                </div>
                <div class="command-category-items">
                  <button
                    v-for="result in results.recent"
                    :key="`recent-${result.type}-${result.id}`"
                    class="command-result-item"
                    :class="{ selected: isSelected(result) }"
                    tabindex="-1"
                    @click="handleResultClick(result)"
                    @mouseenter="selectedIndex = getResultIndex(result)"
                  >
                    <i :class="result.icon" class="result-icon"></i>
                    <div class="result-content">
                      <span class="result-title">{{ result.title }}</span>
                      <span v-if="result.subtitle" class="result-subtitle">{{ result.subtitle }}</span>
                    </div>
                    <span class="result-type-badge">{{ result.type }}</span>
                  </button>
                </div>
              </div>

              <!-- Recent Searches -->
              <div v-if="recentSearches.length > 0" class="command-category">
                <div class="command-category-header">
                  <i class="bi bi-search me-2"></i>
                  Recent Searches
                  <button class="btn-clear-recent" tabindex="-1" @click.stop="clearRecentSearches">Clear</button>
                </div>
                <div class="command-category-items">
                  <button
                    v-for="recent in recentSearches"
                    :key="recent"
                    class="command-recent-item"
                    tabindex="-1"
                    @click="handleRecentClick(recent)"
                  >
                    <i class="bi bi-arrow-return-right me-2 text-muted"></i>
                    {{ recent }}
                  </button>
                </div>
              </div>
            </template>

            <!-- Search results -->
            <template v-else>
              <!-- Quick Actions -->
              <div v-if="shouldShowCategory(results.actions)" class="command-category">
                <div class="command-category-header">
                  <i :class="getCategoryIcon('action')" class="me-2"></i>
                  {{ getCategoryLabel('action') }}
                  <span class="category-count">({{ results.actions.length }})</span>
                </div>
                <div class="command-category-items">
                  <button
                    v-for="result in results.actions"
                    :key="result.id"
                    class="command-result-item"
                    :class="{ selected: isSelected(result) }"
                    tabindex="-1"
                    @click="handleResultClick(result)"
                    @mouseenter="selectedIndex = getResultIndex(result)"
                  >
                    <i :class="result.icon" class="result-icon"></i>
                    <span class="result-title" v-html="highlightMatch(result.title, searchQuery)"></span>
                    <kbd v-if="result.shortcut" class="result-shortcut">{{ formatShortcut(result.shortcut) }}</kbd>
                  </button>
                </div>
              </div>

              <!-- Recent Items (when searching) -->
              <div v-if="shouldShowCategory(results.recent)" class="command-category">
                <div class="command-category-header">
                  <i :class="getCategoryIcon('recent')" class="me-2"></i>
                  {{ getCategoryLabel('recent') }}
                  <span class="category-count">({{ results.recent.length }})</span>
                </div>
                <div class="command-category-items">
                  <button
                    v-for="result in results.recent"
                    :key="`search-recent-${result.type}-${result.id}`"
                    class="command-result-item"
                    :class="{ selected: isSelected(result) }"
                    tabindex="-1"
                    @click="handleResultClick(result)"
                    @mouseenter="selectedIndex = getResultIndex(result)"
                  >
                    <i :class="result.icon" class="result-icon"></i>
                    <div class="result-content">
                      <span class="result-title" v-html="highlightMatch(result.title, searchQuery)"></span>
                      <span v-if="result.subtitle" class="result-subtitle" v-html="highlightMatch(result.subtitle, searchQuery)"></span>
                    </div>
                    <span class="result-type-badge">{{ result.type }}</span>
                  </button>
                </div>
              </div>

              <!-- Jobs -->
              <div v-if="shouldShowCategory(results.jobs)" class="command-category">
                <div class="command-category-header">
                  <i :class="getCategoryIcon('job')" class="me-2"></i>
                  {{ getCategoryLabel('job') }}
                  <span class="category-count">({{ results.jobs.length }})</span>
                </div>
                <div class="command-category-items">
                  <button
                    v-for="result in results.jobs"
                    :key="result.id"
                    class="command-result-item"
                    :class="{ selected: isSelected(result) }"
                    tabindex="-1"
                    @click="handleResultClick(result)"
                    @mouseenter="selectedIndex = getResultIndex(result)"
                  >
                    <i :class="result.icon" class="result-icon"></i>
                    <div class="result-content">
                      <span class="result-title" v-html="highlightMatch(result.title, searchQuery)"></span>
                      <span v-if="result.subtitle" class="result-subtitle" v-html="highlightMatch(result.subtitle || '', searchQuery)"></span>
                    </div>
                    <span v-if="result.badge" class="badge" :class="result.badgeClass">{{ result.badge }}</span>
                  </button>
                </div>
              </div>

              <!-- Suppliers -->
              <div v-if="shouldShowCategory(results.suppliers)" class="command-category">
                <div class="command-category-header">
                  <i :class="getCategoryIcon('supplier')" class="me-2"></i>
                  {{ getCategoryLabel('supplier') }}
                  <span class="category-count">({{ results.suppliers.length }})</span>
                </div>
                <div class="command-category-items">
                  <button
                    v-for="result in results.suppliers"
                    :key="result.id"
                    class="command-result-item"
                    :class="{ selected: isSelected(result) }"
                    tabindex="-1"
                    @click="handleResultClick(result)"
                    @mouseenter="selectedIndex = getResultIndex(result)"
                  >
                    <i :class="result.icon" class="result-icon"></i>
                    <div class="result-content">
                      <span class="result-title" v-html="highlightMatch(result.title, searchQuery)"></span>
                      <span v-if="result.subtitle" class="result-subtitle" v-html="highlightMatch(result.subtitle || '', searchQuery)"></span>
                    </div>
                    <span v-if="result.badge" class="badge" :class="result.badgeClass">{{ result.badge }}</span>
                  </button>
                </div>
              </div>

              <!-- Contacts -->
              <div v-if="shouldShowCategory(results.contacts)" class="command-category">
                <div class="command-category-header">
                  <i :class="getCategoryIcon('contact')" class="me-2"></i>
                  {{ getCategoryLabel('contact') }}
                  <span class="category-count">({{ results.contacts.length }})</span>
                </div>
                <div class="command-category-items">
                  <button
                    v-for="result in results.contacts"
                    :key="result.id"
                    class="command-result-item"
                    :class="{ selected: isSelected(result) }"
                    tabindex="-1"
                    @click="handleResultClick(result)"
                    @mouseenter="selectedIndex = getResultIndex(result)"
                  >
                    <i :class="result.icon" class="result-icon"></i>
                    <div class="result-content">
                      <span class="result-title" v-html="highlightMatch(result.title, searchQuery)"></span>
                      <span v-if="result.subtitle" class="result-subtitle" v-html="highlightMatch(result.subtitle || '', searchQuery)"></span>
                    </div>
                    <span v-if="result.badge" class="badge" :class="result.badgeClass">{{ result.badge }}</span>
                  </button>
                </div>
              </div>

              <!-- Documents -->
              <div v-if="shouldShowCategory(results.documents)" class="command-category">
                <div class="command-category-header">
                  <i :class="getCategoryIcon('document')" class="me-2"></i>
                  {{ getCategoryLabel('document') }}
                  <span class="category-count">({{ results.documents.length }})</span>
                </div>
                <div class="command-category-items">
                  <button
                    v-for="result in results.documents"
                    :key="result.id"
                    class="command-result-item"
                    :class="{ selected: isSelected(result) }"
                    tabindex="-1"
                    @click="handleResultClick(result)"
                    @mouseenter="selectedIndex = getResultIndex(result)"
                  >
                    <i :class="result.icon" class="result-icon"></i>
                    <div class="result-content">
                      <span class="result-title" v-html="highlightMatch(result.title, searchQuery)"></span>
                      <span v-if="result.subtitle" class="result-subtitle" v-html="highlightMatch(result.subtitle || '', searchQuery)"></span>
                    </div>
                    <span v-if="result.badge" class="badge" :class="result.badgeClass">{{ result.badge }}</span>
                  </button>
                </div>
              </div>

              <!-- Catalogue -->
              <div v-if="shouldShowCategory(results.catalogue)" class="command-category">
                <div class="command-category-header">
                  <i :class="getCategoryIcon('catalogue')" class="me-2"></i>
                  {{ getCategoryLabel('catalogue') }}
                  <span class="category-count">({{ results.catalogue.length }})</span>
                </div>
                <div class="command-category-items">
                  <button
                    v-for="result in results.catalogue"
                    :key="result.id"
                    class="command-result-item"
                    :class="{ selected: isSelected(result) }"
                    tabindex="-1"
                    @click="handleResultClick(result)"
                    @mouseenter="selectedIndex = getResultIndex(result)"
                  >
                    <i :class="result.icon" class="result-icon"></i>
                    <div class="result-content">
                      <span class="result-title" v-html="highlightMatch(result.title, searchQuery)"></span>
                      <span v-if="result.subtitle" class="result-subtitle" v-html="highlightMatch(result.subtitle || '', searchQuery)"></span>
                    </div>
                    <span v-if="result.badge" class="badge" :class="result.badgeClass">{{ result.badge }}</span>
                  </button>
                </div>
              </div>

              <!-- Orders -->
              <div v-if="shouldShowCategory(results.orders)" class="command-category">
                <div class="command-category-header">
                  <i :class="getCategoryIcon('order')" class="me-2"></i>
                  {{ getCategoryLabel('order') }}
                  <span class="category-count">({{ results.orders.length }})</span>
                </div>
                <div class="command-category-items">
                  <button
                    v-for="result in results.orders"
                    :key="result.id"
                    class="command-result-item"
                    :class="{ selected: isSelected(result) }"
                    tabindex="-1"
                    @click="handleResultClick(result)"
                    @mouseenter="selectedIndex = getResultIndex(result)"
                  >
                    <i :class="result.icon" class="result-icon"></i>
                    <div class="result-content">
                      <span class="result-title" v-html="highlightMatch(result.title, searchQuery)"></span>
                      <span v-if="result.subtitle" class="result-subtitle" v-html="highlightMatch(result.subtitle || '', searchQuery)"></span>
                    </div>
                    <span v-if="result.badge" class="badge" :class="result.badgeClass">{{ result.badge }}</span>
                  </button>
                </div>
              </div>

              <!-- No results -->
              <div
                v-if="!loading && allResults.length === 0"
                class="command-bar-empty"
              >
                <i class="bi bi-search me-2"></i>
                No results found for "{{ searchQuery }}"
                <div class="empty-hint">
                  Try a different search term or type <kbd>&gt;</kbd> to see all commands
                </div>
              </div>
            </template>
          </div>

          <!-- Footer -->
          <div class="command-bar-footer">
            <span class="shortcut-hint">
              <kbd class="arrow-key">&#9650;</kbd><kbd class="arrow-key">&#9660;</kbd> navigate
            </span>
            <span class="shortcut-hint">
              <kbd>Enter</kbd> select
            </span>
            <span class="shortcut-hint">
              <kbd>Esc</kbd> close
            </span>
            <span class="shortcut-hint">
              <kbd>?</kbd> shortcuts
            </span>
          </div>
        </div>
      </div>
    </Transition>

    <!-- Keyboard Shortcuts Help Modal -->
    <Transition name="fade">
      <div v-if="showShortcutsHelp" class="shortcuts-overlay" @click.self="closeShortcutsHelp">
        <div class="shortcuts-modal">
          <div class="shortcuts-header">
            <h5 class="mb-0">
              <i class="bi bi-keyboard me-2"></i>
              Keyboard Shortcuts
            </h5>
            <button class="btn-close" @click="closeShortcutsHelp"></button>
          </div>
          <div class="shortcuts-body">
            <div class="shortcuts-section">
              <h6 class="shortcuts-section-title">Command Bar</h6>
              <div class="shortcut-row">
                <span class="shortcut-desc">Open Command Bar</span>
                <kbd>Ctrl + K</kbd>
              </div>
              <div class="shortcut-row">
                <span class="shortcut-desc">Command Mode</span>
                <kbd>Ctrl + Shift + P</kbd>
              </div>
              <div class="shortcut-row">
                <span class="shortcut-desc">Close</span>
                <kbd>Esc</kbd>
              </div>
            </div>

            <div class="shortcuts-section">
              <h6 class="shortcuts-section-title">Navigation</h6>
              <div class="shortcut-row">
                <span class="shortcut-desc">Go to Dashboard</span>
                <kbd>G</kbd> then <kbd>D</kbd>
              </div>
              <div class="shortcut-row">
                <span class="shortcut-desc">Go to Jobs</span>
                <kbd>G</kbd> then <kbd>J</kbd>
              </div>
              <div class="shortcut-row">
                <span class="shortcut-desc">Go to Suppliers</span>
                <kbd>G</kbd> then <kbd>S</kbd>
              </div>
              <div class="shortcut-row">
                <span class="shortcut-desc">Go to Contacts</span>
                <kbd>G</kbd> then <kbd>C</kbd>
              </div>
              <div class="shortcut-row">
                <span class="shortcut-desc">Go to Documents</span>
                <kbd>G</kbd> then <kbd>F</kbd>
              </div>
              <div class="shortcut-row">
                <span class="shortcut-desc">Go to Settings</span>
                <kbd>G</kbd> then <kbd>,</kbd>
              </div>
            </div>

            <div class="shortcuts-section">
              <h6 class="shortcuts-section-title">Create</h6>
              <div class="shortcut-row">
                <span class="shortcut-desc">New Job</span>
                <kbd>N</kbd> then <kbd>J</kbd>
              </div>
              <div class="shortcut-row">
                <span class="shortcut-desc">New Supplier</span>
                <kbd>N</kbd> then <kbd>S</kbd>
              </div>
              <div class="shortcut-row">
                <span class="shortcut-desc">New Contact</span>
                <kbd>N</kbd> then <kbd>C</kbd>
              </div>
              <div class="shortcut-row">
                <span class="shortcut-desc">Upload Document</span>
                <kbd>N</kbd> then <kbd>D</kbd>
              </div>
            </div>

            <div class="shortcuts-section">
              <h6 class="shortcuts-section-title">Actions</h6>
              <div class="shortcut-row">
                <span class="shortcut-desc">Toggle Dark Mode</span>
                <kbd>Ctrl + Shift + L</kbd>
              </div>
              <div class="shortcut-row">
                <span class="shortcut-desc">Export to CSV</span>
                <kbd>Ctrl + Shift + E</kbd>
              </div>
              <div class="shortcut-row">
                <span class="shortcut-desc">Refresh Data</span>
                <kbd>Ctrl + Shift + R</kbd>
              </div>
              <div class="shortcut-row">
                <span class="shortcut-desc">Show This Help</span>
                <kbd>?</kbd>
              </div>
            </div>
          </div>
          <div class="shortcuts-footer">
            <small class="text-muted">
              Sequential shortcuts (like G then D) must be pressed within 500ms
            </small>
          </div>
        </div>
      </div>
    </Transition>
  </Teleport>
</template>

<style scoped>
.command-bar-overlay {
  position: fixed;
  inset: 0;
  background-color: rgba(0, 0, 0, 0.5);
  display: flex;
  justify-content: center;
  padding-top: 10vh;
  z-index: 9999;
  backdrop-filter: blur(2px);
}

.command-bar {
  width: 600px;
  max-height: 70vh;
  background: var(--bs-body-bg, #ffffff);
  border-radius: 12px;
  box-shadow: 0 25px 50px -12px rgba(0, 0, 0, 0.25);
  display: flex;
  flex-direction: column;
  overflow: hidden;
  border: 1px solid var(--bs-border-color, #e5e7eb);
}

.command-bar-header {
  display: flex;
  align-items: center;
  padding: 16px;
  border-bottom: 1px solid var(--bs-border-color, #e5e7eb);
  gap: 12px;
}

.search-icon {
  color: var(--bs-secondary-color, #6b7280);
  font-size: 1.1rem;
}

.command-bar-input {
  flex: 1;
  border: none;
  outline: none;
  font-size: 1rem;
  color: var(--bs-body-color, #111827);
  background: transparent;
}

.command-bar-input::placeholder {
  color: var(--bs-secondary-color, #9ca3af);
}

.header-badges {
  display: flex;
  align-items: center;
  gap: 8px;
}

.mode-badge {
  padding: 2px 8px;
  font-size: 0.7rem;
  background: var(--bs-primary);
  color: white;
  border-radius: 4px;
  display: flex;
  align-items: center;
  gap: 4px;
}

.esc-hint {
  padding: 2px 6px;
  font-size: 0.75rem;
  background: var(--bs-tertiary-bg, #f3f4f6);
  border: 1px solid var(--bs-border-color, #e5e7eb);
  border-radius: 4px;
  color: var(--bs-secondary-color, #6b7280);
}

.command-bar-results {
  flex: 1;
  overflow-y: auto;
  padding: 8px;
}

.command-bar-loading {
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 24px;
  color: var(--bs-secondary-color, #6b7280);
}

.command-category {
  margin-bottom: 8px;
}

.command-category-header {
  display: flex;
  align-items: center;
  padding: 8px 12px;
  font-size: 0.75rem;
  font-weight: 600;
  text-transform: uppercase;
  color: var(--bs-secondary-color, #6b7280);
  letter-spacing: 0.05em;
}

.category-count {
  margin-left: 4px;
  font-weight: normal;
}

.category-hint {
  margin-left: auto;
  font-weight: normal;
  text-transform: none;
  font-size: 0.7rem;
  opacity: 0.7;
}

.btn-clear-recent {
  margin-left: auto;
  padding: 2px 8px;
  font-size: 0.7rem;
  text-transform: none;
  color: var(--bs-primary, #3b82f6);
  background: none;
  border: none;
  cursor: pointer;
}

.btn-clear-recent:hover {
  text-decoration: underline;
}

.command-category-items {
  display: flex;
  flex-direction: column;
}

.command-result-item,
.command-recent-item {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 10px 12px;
  border: none;
  background: none;
  width: 100%;
  text-align: left;
  cursor: pointer;
  border-radius: 8px;
  transition: background-color 0.1s;
  color: var(--bs-body-color, inherit);
}

.command-result-item:hover,
.command-recent-item:hover,
.command-result-item.selected {
  background-color: var(--bs-primary-bg-subtle, #e7f1ff);
}

.result-icon {
  color: var(--bs-secondary-color, #6b7280);
  font-size: 1rem;
  flex-shrink: 0;
}

.result-content {
  flex: 1;
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: 2px;
}

.result-title {
  font-weight: 500;
  color: var(--bs-body-color, #111827);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.result-title :deep(mark) {
  background-color: var(--bs-warning-bg-subtle, #fef3c7);
  color: inherit;
  padding: 0 2px;
  border-radius: 2px;
}

.result-subtitle {
  font-size: 0.85rem;
  color: var(--bs-secondary-color, #6b7280);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.result-subtitle :deep(mark) {
  background-color: var(--bs-warning-bg-subtle, #fef3c7);
  color: inherit;
  padding: 0 2px;
  border-radius: 2px;
}

.result-shortcut {
  padding: 2px 6px;
  font-size: 0.65rem;
  background: var(--bs-tertiary-bg, #f3f4f6);
  border: 1px solid var(--bs-border-color, #e5e7eb);
  border-radius: 4px;
  color: var(--bs-secondary-color, #6b7280);
  font-family: inherit;
  flex-shrink: 0;
}

.result-type-badge {
  padding: 2px 6px;
  font-size: 0.6rem;
  background: var(--bs-tertiary-bg, #f3f4f6);
  border-radius: 4px;
  color: var(--bs-secondary-color, #6b7280);
  text-transform: uppercase;
  flex-shrink: 0;
}

.command-bar-empty {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 32px;
  color: var(--bs-secondary-color, #6b7280);
  text-align: center;
}

.empty-hint {
  margin-top: 8px;
  font-size: 0.85rem;
  opacity: 0.7;
}

.empty-hint kbd {
  padding: 1px 4px;
  font-size: 0.75rem;
  background: var(--bs-tertiary-bg, #f3f4f6);
  border: 1px solid var(--bs-border-color, #e5e7eb);
  border-radius: 3px;
  font-family: inherit;
}

.command-bar-footer {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 16px;
  padding: 10px 16px;
  border-top: 1px solid var(--bs-border-color, #e5e7eb);
  background: var(--bs-tertiary-bg, #f9fafb);
}

.shortcut-hint {
  display: flex;
  align-items: center;
  gap: 4px;
  font-size: 0.75rem;
  color: var(--bs-secondary-color, #6b7280);
}

.shortcut-hint kbd {
  padding: 2px 5px;
  font-size: 0.7rem;
  background: var(--bs-body-bg, #ffffff);
  border: 1px solid var(--bs-border-color, #e5e7eb);
  border-radius: 3px;
  font-family: inherit;
  color: var(--bs-body-color, #374151);
}

.shortcut-hint kbd.arrow-key {
  font-size: 0.6rem;
  padding: 2px 4px;
}

/* Shortcuts Help Modal */
.shortcuts-overlay {
  position: fixed;
  inset: 0;
  background-color: rgba(0, 0, 0, 0.5);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 10000;
  backdrop-filter: blur(2px);
}

.shortcuts-modal {
  width: 500px;
  max-height: 80vh;
  background: var(--bs-body-bg, #ffffff);
  border-radius: 12px;
  box-shadow: 0 25px 50px -12px rgba(0, 0, 0, 0.25);
  display: flex;
  flex-direction: column;
  overflow: hidden;
  border: 1px solid var(--bs-border-color, #e5e7eb);
}

.shortcuts-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 16px 20px;
  border-bottom: 1px solid var(--bs-border-color, #e5e7eb);
}

.shortcuts-body {
  flex: 1;
  overflow-y: auto;
  padding: 16px 20px;
}

.shortcuts-section {
  margin-bottom: 20px;
}

.shortcuts-section:last-child {
  margin-bottom: 0;
}

.shortcuts-section-title {
  font-size: 0.75rem;
  font-weight: 600;
  text-transform: uppercase;
  color: var(--bs-secondary-color, #6b7280);
  letter-spacing: 0.05em;
  margin-bottom: 10px;
}

.shortcut-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 8px 0;
  border-bottom: 1px solid var(--bs-border-color-translucent, rgba(0, 0, 0, 0.05));
}

.shortcut-row:last-child {
  border-bottom: none;
}

.shortcut-desc {
  color: var(--bs-body-color, #374151);
  font-size: 0.9rem;
}

.shortcut-row kbd {
  padding: 3px 8px;
  font-size: 0.75rem;
  background: var(--bs-tertiary-bg, #f3f4f6);
  border: 1px solid var(--bs-border-color, #e5e7eb);
  border-radius: 4px;
  font-family: inherit;
  color: var(--bs-body-color, #374151);
  margin-left: 4px;
}

.shortcuts-footer {
  padding: 12px 20px;
  border-top: 1px solid var(--bs-border-color, #e5e7eb);
  background: var(--bs-tertiary-bg, #f9fafb);
  text-align: center;
}

/* Transition */
.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.15s ease;
}

.fade-enter-from,
.fade-leave-to {
  opacity: 0;
}

/* Badge overrides for compact display */
.badge {
  font-size: 0.7rem;
  padding: 3px 6px;
  flex-shrink: 0;
}

/* Dark mode adjustments */
[data-bs-theme="dark"] .command-bar {
  box-shadow: 0 25px 50px -12px rgba(0, 0, 0, 0.5);
}

[data-bs-theme="dark"] .result-title :deep(mark),
[data-bs-theme="dark"] .result-subtitle :deep(mark) {
  background-color: rgba(251, 191, 36, 0.3);
}
</style>
